import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  CurrencyDollar,
  Briefcase,
  Users,
  TrendUp,
  ArrowRight,
  CalendarBlank,
  Receipt,
  Clock,
  Warning,
} from "phosphor-react";
import { formatCurrency } from "../utils/formatters";
import { format, subDays } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const StatCard = ({ title, value, subtitle, icon: Icon, color, link }) => (
  <Card className="border-2 border-zinc-200 hover:border-zinc-300 transition-colors">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs text-zinc-500 uppercase tracking-wide font-medium">{title}</p>
          <p className="font-heading text-2xl font-bold mt-1">{value}</p>
          {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 ${color} flex items-center justify-center rounded`}>
          <Icon size={20} weight="bold" />
        </div>
      </div>
      {link && (
        <Link to={link} className="text-sm text-blue-600 hover:text-blue-700 mt-3 inline-flex items-center gap-1">
          View details <ArrowRight size={14} />
        </Link>
      )}
    </CardContent>
  </Card>
);

export default function Reports() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
  });

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const startISO = new Date(dateRange.start).toISOString();
      const endISO = new Date(dateRange.end + "T23:59:59").toISOString();
      const response = await fetch(
        `${API}/reports/summary?start_date=${startISO}&end_date=${endISO}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error("Error fetching report summary:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading && !summary) {
    return (
      <div className="space-y-6" data-testid="reports-page">
        <div className="animate-pulse">
          <div className="h-8 bg-zinc-200 rounded w-32 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-zinc-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Reports</h1>
          <p className="text-sm text-zinc-500">Track your business performance</p>
        </div>
        
        {/* Date Range Selector */}
        <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-2">
          <CalendarBlank size={18} className="text-zinc-500" />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
            className="bg-transparent border-none text-sm focus:outline-none"
            data-testid="reports-start-date"
          />
          <span className="text-zinc-400">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
            className="bg-transparent border-none text-sm focus:outline-none"
            data-testid="reports-end-date"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Revenue"
          value={formatCurrency(summary?.revenue?.total || 0)}
          subtitle={`${summary?.revenue?.payment_count || 0} payments`}
          icon={CurrencyDollar}
          color="bg-green-100 text-green-600"
          link="/reports/money"
        />
        <StatCard
          title="Outstanding"
          value={formatCurrency(summary?.outstanding?.total || 0)}
          subtitle={`${summary?.outstanding?.invoice_count || 0} invoices`}
          icon={Receipt}
          color="bg-amber-100 text-amber-600"
          link="/reports/money"
        />
        <StatCard
          title="Overdue"
          value={formatCurrency(summary?.overdue?.total || 0)}
          subtitle={`${summary?.overdue?.invoice_count || 0} invoices`}
          icon={Warning}
          color="bg-red-100 text-red-600"
          link="/reports/money"
        />
        <StatCard
          title="Jobs Completed"
          value={summary?.work?.jobs_completed || 0}
          subtitle={`${summary?.work?.visits_completed || 0} visits`}
          icon={Briefcase}
          color="bg-blue-100 text-blue-600"
          link="/reports/work"
        />
      </div>

      {/* Report Sections */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Money Reports */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 flex items-center justify-center rounded">
                <CurrencyDollar size={18} className="text-green-600" weight="bold" />
              </div>
              <CardTitle className="font-heading text-lg">Money</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-zinc-500">Revenue, outstanding, aging</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-600">Total Invoiced</span>
                <span className="font-medium">{formatCurrency(summary?.invoiced?.total || 0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Avg Invoice</span>
                <span className="font-medium">{formatCurrency(summary?.invoiced?.average || 0)}</span>
              </div>
            </div>
            <Link to="/reports/money">
              <Button variant="outline" size="sm" className="w-full mt-2" data-testid="view-money-reports">
                View Money Reports <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Work Reports */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 flex items-center justify-center rounded">
                <Briefcase size={18} className="text-blue-600" weight="bold" />
              </div>
              <CardTitle className="font-heading text-lg">Work</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-zinc-500">Jobs, visits, technician productivity</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-600">Jobs Created</span>
                <span className="font-medium">{summary?.invoiced?.invoice_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Visits Completed</span>
                <span className="font-medium">{summary?.work?.visits_completed || 0}</span>
              </div>
            </div>
            <Link to="/reports/work">
              <Button variant="outline" size="sm" className="w-full mt-2" data-testid="view-work-reports">
                View Work Reports <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Customer Reports */}
        <Card className="border-2 border-zinc-200">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-100 flex items-center justify-center rounded">
                <Users size={18} className="text-purple-600" weight="bold" />
              </div>
              <CardTitle className="font-heading text-lg">Customers</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-zinc-500">Top customers, inactive customers</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-600">Top Revenue</span>
                <span className="font-medium">View list →</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-600">Inactive 90+ days</span>
                <span className="font-medium">View list →</span>
              </div>
            </div>
            <Link to="/reports/customers">
              <Button variant="outline" size="sm" className="w-full mt-2" data-testid="view-customer-reports">
                View Customer Reports <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
